import React, { useState } from 'react';
import { Mail, Instagram, Send, MapPin, Phone } from 'lucide-react';
import { mockData } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { contact } = mockData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        // Track form submission with Google Analytics
        if (window.trackContactForm) {
          window.trackContactForm(formData.subject);
        }
        
        toast({
          title: "Message envoyé !",
          description: "Je vous répondrai dans les plus brefs délais.",
        });
        
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      
      let errorMessage = "Une erreur est survenue lors de l'envoi du message.";
      
      if (error.response?.status === 422) {
        errorMessage = "Veuillez vérifier les informations saisies.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      name: 'Instagram',
      url: contact.social.instagram,
      icon: Instagram,
      color: 'hover:text-pink-500'
    }
  ];

  return (
    <section
      id="contact"
      className="py-20"
      style={{
        backgroundColor: '#FFFFFF',
        backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
        backgroundSize: '4px 4px'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6 tracking-tight">
            {contact.title}
          </h2>
          <div className="w-16 h-1 rounded-full mx-auto mb-8" style={{ backgroundColor: '#f97316' }}></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Parlons de votre projet
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Que vous ayez besoin d'un site web, d'une boutique e-commerce ou d'une production vidéo,
                je vous accompagne de la stratégie à la mise en ligne.
              </p>
            </div>

            {/* Contact Methods */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f97316' }}>
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Email</h4>
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-gray-600 transition-colors"
                    style={{ color: '#f97316' }}
                    onMouseEnter={(e) => e.target.style.color = '#ea580c'}
                    onMouseLeave={(e) => e.target.style.color = '#f97316'}
                  >
                    {contact.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f97316' }}>
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Zone d'intervention</h4>
                  <p className="text-gray-600">France métropolitaine</p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Suivez-moi</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-600 ${social.color} transition-all duration-200 hover:scale-110`}
                  >
                    <social.icon className="h-6 w-6" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quote */}
            <blockquote className="border-l-4 pl-6 py-4 bg-gray-50 rounded-r-lg" style={{ borderLeftColor: '#f97316' }}>
              <p className="text-lg font-medium text-gray-800 italic">
                "Chaque projet mérite une mise en valeur claire et impactante."
              </p>
            </blockquote>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#f97316' }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #f97316'}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#f97316' }}
                    onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #f97316'}
                    onBlur={(e) => e.target.style.boxShadow = ''}
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Type de projet *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                  style={{ '--tw-ring-color': '#f97316' }}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #f97316'}
                  onBlur={(e) => e.target.style.boxShadow = ''}
                >
                  <option value="">Sélectionnez un type de projet</option>
                  <option value="website">Site vitrine</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="performance">Optimisation & SEO</option>
                  <option value="video">Production vidéo</option>
                  <option value="drone">Prises de vues drone</option>
                  <option value="other">Autre projet</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Décrivez votre projet *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none"
                  style={{ '--tw-ring-color': '#f97316' }}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #f97316'}
                  onBlur={(e) => e.target.style.boxShadow = ''}
                  placeholder="Parlez-moi de votre projet : objectifs, délais, budget estimé, exemples..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full text-white py-4 rounded-lg text-lg font-medium transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg flex items-center justify-center space-x-2"
                style={{ backgroundColor: isSubmitting ? '#fb923c' : '#f97316' }}
                onMouseEnter={(e) => !isSubmitting && (e.target.style.backgroundColor = '#ea580c')}
                onMouseLeave={(e) => !isSubmitting && (e.target.style.backgroundColor = '#f97316')}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Envoyer le message</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
